import { Injectable, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ApiService } from "./api.service";
import { AuthService } from "./auth.service";

export interface TourStep {
  title: string;
  body: string;
  /** Route to navigate to before showing this step (optional). */
  route?: string;
  /** CSS selector of the element to spotlight; omitted = centered card. */
  selector?: string;
  /** Only include the step when this returns true (permission gating). */
  when?: () => boolean;
  /** Open the mobile nav drawer so the target is visible on small screens. */
  needsMenu?: boolean;
}

/** Guided office tour: spotlights real UI elements, navigating between pages.
 *  The overlay component renders from these signals; the shell registers a
 *  drawer-open callback so nav highlights work on mobile too. */
@Injectable({ providedIn: "root" })
export class TourService {
  private api = inject(ApiService);
  private auth = inject(AuthService);
  private router = inject(Router);

  active = signal(false);
  index = signal(0);
  steps = signal<TourStep[]>([]);
  targetRect = signal<DOMRect | null>(null);

  private openMenu: ((open: boolean) => void) | null = null;
  private findTimer: ReturnType<typeof setInterval> | null = null;
  private startedThisSession = false;

  constructor() {
    // Tear the tour down if the session ends mid-tour (logout, 401 kick,
    // principal switch) so the overlay can't linger over the login screen.
    effect(() => {
      const u = this.auth.user();
      if (this.active() && (!u || !this.auth.isAdmin())) this.abort();
    });
  }

  registerMenuControl(fn: (open: boolean) => void) {
    this.openMenu = fn;
  }

  /** Close the overlay without persisting completion (used on auth loss). */
  abort() {
    this.stopFinder();
    this.active.set(false);
    this.openMenu?.(false);
  }

  /** Auto-start once per session for admins who never finished the tour. */
  maybeAutoStart() {
    const u = this.auth.user();
    if (
      !this.startedThisSession &&
      !this.active() &&
      u &&
      this.auth.isAdmin() &&
      !u.tour_completed_at
    ) {
      this.start();
    }
  }

  start() {
    if (!this.auth.isAdmin()) return; // admin/owner-only feature
    this.startedThisSession = true;
    this.steps.set(this.buildSteps().filter((s) => !s.when || s.when()));
    this.index.set(0);
    this.active.set(true);
    this.activate();
  }

  next() {
    if (this.index() >= this.steps().length - 1) {
      this.finish();
      return;
    }
    this.index.update((i) => i + 1);
    this.activate();
  }

  prev() {
    if (this.index() === 0) return;
    this.index.update((i) => i - 1);
    this.activate();
  }

  /** Skip and finish both persist completion so the tour stops auto-starting. */
  finish() {
    this.stopFinder();
    this.active.set(false);
    this.openMenu?.(false);
    this.api.completeTour().subscribe({
      next: (u) => this.auth.user.set(u),
      error: () => {},
    });
  }

  current(): TourStep | null {
    return this.steps()[this.index()] ?? null;
  }

  remeasure() {
    const sel = this.current()?.selector;
    if (!sel) return;
    const el = document.querySelector(sel);
    this.targetRect.set(el ? el.getBoundingClientRect() : null);
  }

  private activate() {
    this.stopFinder();
    const step = this.current();
    if (!step) return;
    this.targetRect.set(null);
    if (step.route && this.router.url !== step.route) {
      this.router.navigateByUrl(step.route);
    }
    const narrow = window.innerWidth <= 980;
    if (step.needsMenu && narrow) this.openMenu?.(true);
    else if (narrow) this.openMenu?.(false);
    if (!step.selector) return;
    // The target may not exist yet (lazy route still loading) — poll briefly.
    let tries = 0;
    this.findTimer = setInterval(() => {
      tries++;
      const el = document.querySelector(step.selector!);
      if (el) {
        this.stopFinder();
        (el as HTMLElement).scrollIntoView({ block: "nearest" });
        this.targetRect.set(el.getBoundingClientRect());
      } else if (tries > 40) {
        this.stopFinder(); // give up: show a centered card instead
      }
    }, 100);
  }

  private stopFinder() {
    if (this.findTimer) {
      clearInterval(this.findTimer);
      this.findTimer = null;
    }
  }

  private buildSteps(): TourStep[] {
    const has = (p: string) => this.auth.has(p);
    const admin = () => this.auth.isAdmin();
    return [
      {
        title: "Welcome to Talos Foreman",
        body: "This quick tour shows you where everything lives — projects, clients, documents, scheduling, your team and more. You can leave at any time and relaunch it later from the “Take a tour” button at the bottom of the sidebar.",
      },
      {
        title: "Dashboard",
        body: "Your command center: cashflow tiles, revenue projections, today’s schedule and pending tasks. Every tile is clickable and you can customize which tiles appear.",
        route: "/dashboard",
        selector: '[data-tour="/dashboard"]',
        needsMenu: true,
      },
      {
        title: "Projects — the hub of everything",
        body: "Each project has 8 tabs: overview, its own dashboard, documents (estimates, contracts, proposals and photo gallery), takeoff, a task board, team, client communication and payments. Create a new project from the button at the top of this page.",
        route: "/projects",
        selector: '[data-tour="/projects"]',
        needsMenu: true,
        when: () => has("projects.manage"),
      },
      {
        title: "Clients",
        body: "Manage your client list and invite clients to their own customer portal, where they can see their projects and invoices — and sign documents online.",
        route: "/clients",
        selector: '[data-tour="/clients"]',
        needsMenu: true,
        when: () => has("clients.manage"),
      },
      {
        title: "Site Proposals & documents",
        body: "Capture a site visit with photos, video and voice notes, and the AI drafts a priced proposal you can edit and send for e-signature. Estimates and contracts live inside each project’s Documents tab — an accepted proposal even drafts its contract for you.",
        route: "/proposals",
        selector: '[data-tour="/proposals"]',
        needsMenu: true,
        when: () => has("proposals.manage"),
      },
      {
        title: "Scheduling & team workload",
        body: "Book appointments in list or calendar view, dispatch tasks with them, and check the Team Workload tab to see who has capacity before you assign work.",
        route: "/scheduling",
        selector: '[data-tour="/scheduling"]',
        needsMenu: true,
        when: () => has("scheduling.manage"),
      },
      {
        title: "Team Chat",
        body: "Message any teammate directly or broadcast to the whole company or a project team. The badge shows unread messages.",
        route: "/team",
        selector: '[data-tour="/team"]',
        needsMenu: true,
      },
      {
        title: "Employees, roles & access",
        body: "Add office staff and field crew, then control exactly what each role can see and do from Roles & Access. Crew members get their own simplified “My Work” view and mobile app.",
        route: "/employees",
        selector: '[data-tour="/employees"]',
        needsMenu: true,
        when: () => has("employees.manage"),
      },
      {
        title: "Marketing Hub",
        body: "Track leads in the CRM pipeline, build your company website with AI, and generate ad creatives — all included with your plan.",
        route: "/crm",
        selector: '[data-tour="/crm"]',
        needsMenu: true,
        when: () =>
          has("marketing.manage") &&
          this.auth.user()?.marketing_included !== false,
      },
      {
        title: "Company Settings & Billing",
        body: "Set your branding and logo (they appear on your PDFs), configure online invoice payments, and manage your subscription from Billing.",
        route: "/settings",
        selector: '[data-tour="/settings"]',
        needsMenu: true,
        when: admin,
      },
      {
        title: "You’re all set!",
        body: "That’s the lay of the land. Start by creating a project or adding a client — and remember, you can retake this tour anytime from the “Take a tour” button at the bottom of the sidebar.",
      },
    ];
  }
}
